import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import { cryptiqueAnalyticsTheme as theme } from "../theme";
import Icon from "../../../../shared/components/Icon";

const ADD_TO_BOARD_OPTIONS = [
  {
    id: "insights",
    label: "Insights Report",
    description: "Explore product usage in detail",
    icon: ({ size = 20, strokeWidth = 1.8, ...props }) => <Icon name="analytics" size={size} color={theme.textMuted} {...props} />,
  },
  {
    id: "funnels",
    label: "Funnels Report",
    description: "Analyze conversion and drop-off",
    icon: ({ size = 20, strokeWidth = 1.8, ...props }) => <Icon name="funnel" size={size} color={theme.textMuted} {...props} />,
  },
  {
    id: "flows",
    label: "Flows Report",
    description: "Understand steps in the user journey",
    icon: ({ size = 20, strokeWidth = 1.8, ...props }) => <Icon name="flows" size={size} color={theme.textMuted} {...props} />,
  },
  {
    id: "retention",
    label: "Retention Report",
    description: "Measure product stickiness",
    icon: ({ size = 20, strokeWidth = 1.8, ...props }) => <Icon name="retention" size={size} color={theme.textMuted} {...props} />,
  },
  {
    id: "text",
    label: "Text",
    description: "Use text to tell a story",
    icon: ({ size = 20, strokeWidth = 1.8, ...props }) => <Icon name="text" size={size} color={theme.textMuted} {...props} />,
  },
  {
    id: "media",
    label: "Media",
    description: "Upload an image or embed a video",
    icon: ({ size = 20, strokeWidth = 1.8, ...props }) => <Icon name="media" size={size} color={theme.textMuted} {...props} />,
  },
];

const DROPDOWN_WIDTH = 320;
const DROPDOWN_MAX_HEIGHT = 400;
const GAP = 4;

export default function AddToBoardDropdown({ open, onClose, anchorRef, onSelectType }) {
  const [isOpen, setIsOpen] = useState(!!open);
  const [position, setPosition] = useState(null);
  const navigate = useNavigate();
  const { team, boardId } = useParams();
  const dropdownRef = useRef(null);

  useEffect(() => {
    setIsOpen(!!open);
    if (!open) setPosition(null);
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !anchorRef?.current) return;
    const anchor = anchorRef.current.getBoundingClientRect();
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    const maxH = Math.min(DROPDOWN_MAX_HEIGHT, vh * 0.7);
    const spaceBelow = vh - anchor.bottom;
    const spaceAbove = anchor.top;
    const openUp = spaceBelow < maxH && spaceAbove > spaceBelow;
    let top = openUp ? anchor.top - maxH - GAP : anchor.bottom + GAP;
    if (top < 8) top = 8;
    if (top + maxH > vh - 8) top = vh - maxH - 8;
    let left = anchor.left;
    if (left + DROPDOWN_WIDTH > vw - 8) left = vw - DROPDOWN_WIDTH - 8;
    if (left < 8) left = 8;
    setPosition({ top, left });
  }, [open, anchorRef]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        anchorRef?.current &&
        !anchorRef.current.contains(e.target)
      ) {
        onClose?.();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, onClose, anchorRef]);

  const handleSelect = (id) => {
    onClose?.();

    if (onSelectType) {
      onSelectType(id);
      return;
    }

    const bid = boardId || "new";

    switch (id) {
      case "insights":
        navigate(`/${team}/analytics/boards/${bid}/editor?type=insights`);
        break;
      case "funnels":
        navigate(`/${team}/analytics/boards/${bid}/editor?type=funnels`);
        break;
      case "flows":
        navigate(`/${team}/analytics/boards/${bid}/editor?type=flows`);
        break;
      case "retention":
        navigate(`/${team}/analytics/boards/${bid}/editor?type=retention`);
        break;
      case "text":
      case "media":
        navigate(`/${team}/analytics/boards/${bid}/editor?type=${id}`);
        break;
      default:
        break;
    }
  };

  if (!isOpen || !position) return null;

  const dropdown = (
    <div
      ref={dropdownRef}
      className="min-w-[320px] max-h-[min(400px,70vh)] overflow-y-auto rounded-xl border shadow-xl py-1"
      style={{
        position: "fixed",
        top: position.top,
        left: position.left,
        width: DROPDOWN_WIDTH,
        maxHeight: Math.min(DROPDOWN_MAX_HEIGHT, typeof window !== "undefined" ? window.innerHeight * 0.7 : 400),
        fontFamily: "Raleway, sans-serif",
        backgroundColor: theme.bgRaised,
        borderColor: theme.border,
        zIndex: 9999,
      }}
    >
      <div
        className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider flex-shrink-0"
        style={{ color: theme.textMuted }}
      >
        Add to Board
      </div>
      {ADD_TO_BOARD_OPTIONS.map((item) => {
        const Icon = item.icon;
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => handleSelect(item.id)}
            className="w-full flex items-start gap-3 px-3 py-2.5 text-left transition-colors"
            style={{ color: theme.textSecondary }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = `${theme.primaryLight}12`;
              e.currentTarget.style.color = theme.textPrimary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color = theme.textSecondary;
            }}
          >
            <span className="flex-shrink-0 mt-0.5" style={{ color: theme.textMuted }}>
              <Icon size={18} strokeWidth={1.8} />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-medium" style={{ color: theme.textPrimary }}>
                {item.label}
              </span>
              <span className="block text-xs mt-0.5" style={{ color: theme.textMuted }}>
                {item.description}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );

  return createPortal(dropdown, document.body);
}

export { ADD_TO_BOARD_OPTIONS };
